import React, { useCallback, useEffect, useMemo, useState } from 'react';
import OrderLookupPanel from '../components/OrderLookupPanel';
import {
  Card,
  Table,
  Button,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Space,
  Typography,
  Alert,
  message,
  Tooltip,
  Divider,
  Tabs,
  DatePicker
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  ReloadOutlined,
  TeamOutlined,
  WalletOutlined,
  SettingOutlined,
  ApiOutlined
} from '@ant-design/icons';
import { manualRechargeAPI, agentAPI } from '../services/api';

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

// R4 客服订单记录状态映射: 0 进行中(蓝) / 1 已完成(绿) / 2 已取消(红); 其它值兜底。
const ADMIN_ORDER_STATUS = {
  0: { text: '进行中', color: 'blue' },
  1: { text: '已完成', color: 'green' },
  2: { text: '已取消', color: 'red' }
};

// 客服余额低水位阈值(元): 人工充值专接大额(单笔 > 500), 余额低于此值多半接不住一单, 高亮提示补充。
const LOW_BALANCE_THRESHOLD = 500;

// 日期范围 [dayjs, dayjs] → { from, to }(整日边界; RangePicker 给的是 dayjs 实例, 直接取方法)。
function rangeToParams(range) {
  if (!Array.isArray(range) || range.length !== 2 || !range[0] || !range[1]) {
    return { from: undefined, to: undefined };
  }
  return {
    from: range[0].startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    to: range[1].endOf('day').format('YYYY-MM-DD HH:mm:ss')
  };
}

// 把代理树打平为下拉可用的扁平数组（携带层级名/账号，供选择通道管理员与加余额目标）。
function flattenAgentTree(nodes, depth = 0, output = []) {
  if (!Array.isArray(nodes)) {
    return output;
  }
  for (const node of nodes) {
    if (!node) {
      continue;
    }
    output.push({
      id: Number(node.id),
      agent_name: node.agent_name || '',
      username: node.username || '',
      agent_role_name: node.agent_role_name || '',
      // 透传客服身份/状态字段(若树返回): 供"发放平台币"仅列未禁用客服过滤; 缺失时留 undefined 走全量兜底。
      is_recharge_agent: node.is_recharge_agent,
      status: node.status,
      depth
    });
    if (Array.isArray(node.children) && node.children.length > 0) {
      flattenAgentTree(node.children, depth + 1, output);
    }
  }
  return output;
}

function ManualRechargeManagement() {
  const [unifiedLookupActive, setUnifiedLookupActive] = useState(false);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agentOptions, setAgentOptions] = useState([]);

  const [createVisible, setCreateVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [weightVisible, setWeightVisible] = useState(false);
  const [grantVisible, setGrantVisible] = useState(false);

  const [currentChannel, setCurrentChannel] = useState(null);
  const [weightRows, setWeightRows] = useState([]);

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [grantForm] = Form.useForm();

  // R4: Tabs 主体 —— 通道管理 / 发放记录 / 客服订单记录。
  const [activeTab, setActiveTab] = useState('channels');

  // Tab2 发放记录(服务端分页 + 可选筛选; 懒加载)。
  const [grants, setGrants] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [grantsLoaded, setGrantsLoaded] = useState(false);
  const [grantsPagination, setGrantsPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [grantsFilterAgent, setGrantsFilterAgent] = useState(undefined);
  const [grantsRange, setGrantsRange] = useState(null);

  // Tab3 客服订单记录(服务端分页 + 筛选; 懒加载)。
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [ordersPagination, setOrdersPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [ordersFilterAgent, setOrdersFilterAgent] = useState(undefined);
  const [ordersFilterStatus, setOrdersFilterStatus] = useState(undefined);
  const [ordersFilterAccount, setOrdersFilterAccount] = useState('');
  const [ordersRange, setOrdersRange] = useState(null);

  // Tab4 客服余额: 所有人工客服(含通道管理员)+ 各自平台币钱包余额(懒加载, 一次性拉全量)。
  const [balances, setBalances] = useState([]);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [balancesLoaded, setBalancesLoaded] = useState(false);

  const loadChannels = useCallback(async () => {
    setLoading(true);
    try {
      const response = await manualRechargeAPI.getChannels();
      if (response.data.success) {
        setChannels(response.data.data?.channels || []);
      }
    } catch (error) {
      console.error('Load manual recharge channels error:', error);
      message.error(error.response?.data?.message || '加载人工充值通道失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAgents = useCallback(async () => {
    try {
      const response = await agentAPI.getTree();
      if (response.data.success) {
        setAgentOptions(flattenAgentTree(response.data.data || []));
      }
    } catch (error) {
      console.error('Load agent tree error:', error);
    }
  }, []);

  useEffect(() => {
    loadChannels();
    loadAgents();
  }, [loadChannels, loadAgents]);

  // Tab2 发放记录: 拉取(服务端分页; 读嵌套信封 data.{items,total,page,pageSize})。
  const loadGrants = useCallback(async ({ page = 1, pageSize = 20, agentId, range } = {}) => {
    setGrantsLoading(true);
    try {
      const { from, to } = rangeToParams(range);
      const response = await manualRechargeAPI.listAdminGrants({ page, pageSize, agentId, from, to });
      if (response.data.success) {
        const data = response.data.data || {};
        setGrants(Array.isArray(data.items) ? data.items : []);
        setGrantsPagination({
          current: Number(data.page || page),
          pageSize: Number(data.pageSize || pageSize),
          total: Number(data.total || 0)
        });
        setGrantsLoaded(true);
      }
    } catch (error) {
      console.error('Load admin grants error:', error);
      message.error(error.response?.data?.message || '加载发放记录失败');
    } finally {
      setGrantsLoading(false);
    }
  }, []);

  // Tab3 客服订单记录: 拉取(服务端分页 + 筛选; 读嵌套信封 data.{items,total,page,pageSize})。
  const loadOrders = useCallback(async ({ page = 1, pageSize = 20, agentId, status, account, range } = {}) => {
    setOrdersLoading(true);
    try {
      const { from, to } = rangeToParams(range);
      const response = await manualRechargeAPI.listAdminOrders({
        page,
        pageSize,
        agentId,
        status,
        account: String(account || '').trim() || undefined,
        from,
        to
      });
      if (response.data.success) {
        const data = response.data.data || {};
        setOrders(Array.isArray(data.items) ? data.items : []);
        setOrdersPagination({
          current: Number(data.page || page),
          pageSize: Number(data.pageSize || pageSize),
          total: Number(data.total || 0)
        });
        setOrdersLoaded(true);
      }
    } catch (error) {
      console.error('Load admin orders error:', error);
      message.error(error.response?.data?.message || '加载客服订单记录失败');
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  // Tab4 客服余额: 拉取全部人工客服(含通道管理员)+ 余额(读信封 data.agents)。
  const loadBalances = useCallback(async () => {
    setBalancesLoading(true);
    try {
      const response = await manualRechargeAPI.listAgentBalances();
      if (response.data.success) {
        const data = response.data.data || {};
        setBalances(Array.isArray(data.agents) ? data.agents : []);
        setBalancesLoaded(true);
      }
    } catch (error) {
      console.error('Load agent balances error:', error);
      message.error(error.response?.data?.message || '加载客服余额失败');
    } finally {
      setBalancesLoading(false);
    }
  }, []);

  // Tab 懒加载: 切到「发放记录」/「客服订单记录」/「客服余额」且尚未加载过时首拉。
  useEffect(() => {
    if (activeTab === 'grants' && !grantsLoaded) {
      loadGrants({ page: 1, pageSize: grantsPagination.pageSize });
    } else if (activeTab === 'orders' && !ordersLoaded) {
      loadOrders({ page: 1, pageSize: ordersPagination.pageSize });
    } else if (activeTab === 'balances' && !balancesLoaded) {
      loadBalances();
    }
  }, [activeTab, grantsLoaded, ordersLoaded, balancesLoaded, loadGrants, loadOrders, loadBalances, grantsPagination.pageSize, ordersPagination.pageSize]);

  const handleGrantsTableChange = (pag) => {
    loadGrants({ page: pag.current, pageSize: pag.pageSize, agentId: grantsFilterAgent, range: grantsRange });
  };

  const handleOrdersTableChange = (pag) => {
    loadOrders({
      page: pag.current,
      pageSize: pag.pageSize,
      agentId: ordersFilterAgent,
      status: ordersFilterStatus,
      account: ordersFilterAccount,
      range: ordersRange
    });
  };

  const agentSelectOptions = useMemo(() => agentOptions.map((agent) => ({
    value: agent.id,
    label: `${'　'.repeat(agent.depth)}${agent.agent_role_name ? `[${agent.agent_role_name}] ` : ''}${agent.agent_name}（ID ${agent.id}${agent.username ? ` · ${agent.username}` : ''}）`
  })), [agentOptions]);

  // 发放平台币目标: 仅列"未禁用的客服"。若树返回含 is_recharge_agent+status → filter(客服=1 且 status=1);
  //   若字段缺失(拿不到)则保留全量, 依赖后端守卫拦截并由 handleGrant 的 catch 展示后端 400 文案。
  const grantAgentOptions = useMemo(() => {
    const hasStatusFields = agentOptions.some((a) => (
      a.is_recharge_agent !== undefined && a.is_recharge_agent !== null
      && a.status !== undefined && a.status !== null
    ));
    const list = hasStatusFields
      ? agentOptions.filter((a) => Number(a.is_recharge_agent) === 1 && Number(a.status) === 1)
      : agentOptions;
    return list.map((agent) => ({
      value: agent.id,
      label: `${'　'.repeat(agent.depth)}${agent.agent_role_name ? `[${agent.agent_role_name}] ` : ''}${agent.agent_name}（ID ${agent.id}${agent.username ? ` · ${agent.username}` : ''}）`
    }));
  }, [agentOptions]);

  const agentNameById = useMemo(() => {
    const map = new Map();
    for (const agent of agentOptions) {
      map.set(Number(agent.id), agent.agent_name || `代理#${agent.id}`);
    }
    return map;
  }, [agentOptions]);

  const handleCreate = async (values) => {
    try {
      const response = await manualRechargeAPI.createChannel({
        name: values.name,
        adminAgentId: values.adminAgentId,
        displayNameEn: values.displayNameEn,
        displayNameAr: values.displayNameAr,
        minAmount: values.minAmount,
        maxAmount: values.maxAmount,
        sortOrder: values.sortOrder
      });
      if (response.data.success) {
        message.success('通道创建成功（通道管理员已标记为客服身份，默认权重100，默认关闭）');
        setCreateVisible(false);
        createForm.resetFields();
        loadChannels();
      }
    } catch (error) {
      console.error('Create channel error:', error);
      message.error(error.response?.data?.message || '创建通道失败');
    }
  };

  const openEdit = (channel) => {
    setCurrentChannel(channel);
    editForm.setFieldsValue({
      name: channel.name,
      displayNameEn: channel.displayNameEn,
      displayNameAr: channel.displayNameAr,
      minAmount: channel.minAmount || 0,
      maxAmount: channel.maxAmount || 0,
      sortOrder: channel.sortOrder || 0
    });
    setEditVisible(true);
  };

  const handleEdit = async (values) => {
    if (!currentChannel) {
      return;
    }
    try {
      const response = await manualRechargeAPI.updateChannel(currentChannel.id, {
        name: values.name,
        displayNameEn: values.displayNameEn,
        displayNameAr: values.displayNameAr,
        minAmount: values.minAmount,
        maxAmount: values.maxAmount,
        sortOrder: values.sortOrder
      });
      if (response.data.success) {
        message.success('通道已更新');
        setEditVisible(false);
        loadChannels();
      }
    } catch (error) {
      console.error('Update channel error:', error);
      message.error(error.response?.data?.message || '更新通道失败');
    }
  };

  const handleToggleEnabled = async (channel, enabled) => {
    try {
      const response = await manualRechargeAPI.updateChannel(channel.id, { enabled });
      if (response.data.success) {
        message.success(enabled ? '通道已启用' : '通道已关闭');
        loadChannels();
      }
    } catch (error) {
      console.error('Toggle channel error:', error);
      message.error(error.response?.data?.message || '切换通道开关失败');
    }
  };

  const openWeights = (channel) => {
    setCurrentChannel(channel);
    const members = Array.isArray(channel.members) ? channel.members : [];
    setWeightRows(members.map((member) => ({
      agentId: Number(member.id),
      nickname: member.nickname || member.agentName || member.username || `代理#${member.id}`,
      isAdmin: !!member.isAdmin,
      isRechargeAgent: Number(member.isRechargeAgent || 0) === 1,
      weight: Number(member.weight || 0),
      // 需求1(2026-08-12): 该成员平台币钱包最新余额(agent_coin_wallet.balance, 与发货/补单扣款同一账本), 供管理员及时补充。
      balance: Number(member.balance || 0)
    })));
    setWeightVisible(true);
  };

  const weightSum = useMemo(
    () => weightRows.reduce((sum, row) => sum + (Number(row.weight) || 0), 0),
    [weightRows]
  );

  const handleWeightChange = (agentId, value) => {
    setWeightRows((rows) => rows.map((row) => (
      row.agentId === agentId ? { ...row, weight: Number(value) || 0 } : row
    )));
  };

  const handleEqualize = () => {
    if (weightRows.length === 0) {
      return;
    }
    const base = Math.floor(100 / weightRows.length);
    const remainder = 100 - base * weightRows.length;
    // 余数归通道管理员（若无管理员则归第一行）
    const adminIndex = weightRows.findIndex((row) => row.isAdmin);
    const bonusIndex = adminIndex >= 0 ? adminIndex : 0;
    setWeightRows((rows) => rows.map((row, index) => ({
      ...row,
      weight: base + (index === bonusIndex ? remainder : 0)
    })));
  };

  const handleSaveWeights = async () => {
    if (!currentChannel) {
      return;
    }
    if (weightSum !== 100) {
      message.error(`权重之和必须等于 100（当前 ${weightSum}）`);
      return;
    }
    try {
      const response = await manualRechargeAPI.setWeights(currentChannel.id, {
        weights: weightRows.map((row) => ({ agentId: row.agentId, weight: Number(row.weight) || 0 }))
      });
      if (response.data.success) {
        message.success('权重配置已保存（权重>0 的成员已自动标记为客服身份）');
        setWeightVisible(false);
        loadChannels();
      }
    } catch (error) {
      console.error('Save weights error:', error);
      message.error(error.response?.data?.message || '保存权重失败');
    }
  };

  const handleGrant = async (values) => {
    try {
      const requestId = `GRANT_UI_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const response = await manualRechargeAPI.grant(values.agentId, {
        amount: values.amount,
        remark: values.remark,
        requestId
      });
      if (response.data.success) {
        const balance = response.data.data?.balance;
        message.success(`加余额成功${balance !== undefined ? `，当前余额 ¥${Number(balance).toFixed(2)}` : ''}`);
        setGrantVisible(false);
        grantForm.resetFields();
        // 发放后刷新「客服余额」总览: 已加载过则原地重拉, 否则置未加载态下次进入自动拉取。
        if (balancesLoaded) {
          loadBalances();
        } else {
          setBalancesLoaded(false);
        }
      }
    } catch (error) {
      console.error('Grant agent balance error:', error);
      message.error(error.response?.data?.message || '加余额失败');
    }
  };

  const columns = [
    {
      title: '通道',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div className="table-cell">
          <div className="table-cell-primary">{name || '-'}</div>
          <div className="table-cell-secondary"><Text code>{record.channelKey}</Text></div>
          {(record.displayNameEn || record.displayNameAr) ? (
            <div className="table-cell-meta">{record.displayNameEn} {record.displayNameAr}</div>
          ) : null}
        </div>
      )
    },
    {
      title: '通道管理员',
      dataIndex: 'adminAgentId',
      key: 'adminAgentId',
      width: 200,
      render: (adminAgentId) => (
        <span>{agentNameById.get(Number(adminAgentId)) || '-'}（ID {adminAgentId}）</span>
      )
    },
    {
      title: '金额范围',
      key: 'amountRange',
      width: 160,
      render: (_, record) => (
        <span>
          ¥{Number(record.minAmount || 0).toFixed(2)} ~ {Number(record.maxAmount || 0) > 0 ? `¥${Number(record.maxAmount).toFixed(2)}` : '不限'}
        </span>
      )
    },
    {
      title: '成员',
      dataIndex: 'members',
      key: 'members',
      width: 100,
      render: (members) => <Tag color="blue">{Array.isArray(members) ? members.length : 0} 人</Tag>
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80
    },
    {
      title: '开关',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled, record) => (
        <Switch
          checked={!!enabled}
          checkedChildren="启用"
          unCheckedChildren="关闭"
          onChange={(checked) => handleToggleEnabled(record, checked)}
        />
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space>
          <Tooltip title="编辑通道">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>编辑</Button>
          </Tooltip>
          <Tooltip title="配置成员权重（和须=100）">
            <Button size="small" icon={<TeamOutlined />} onClick={() => openWeights(record)}>权重</Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  // Tab2 发放记录列。
  const grantColumns = [
    { title: '时间', dataIndex: 'createTime', key: 'createTime', width: 180, render: (t) => (t ? new Date(t).toLocaleString('zh-CN') : '-') },
    {
      title: '客服',
      key: 'agent',
      width: 220,
      render: (_, r) => <span>{r.agentName || `代理#${r.agentId}`}（ID {r.agentId}）</span>
    },
    { title: '变动金额', dataIndex: 'changeAmount', key: 'changeAmount', width: 130, render: (a) => <Text strong style={{ color: '#16a34a' }}>+¥{Number(a || 0).toFixed(2)}</Text> },
    { title: '发放后余额', dataIndex: 'balanceAfter', key: 'balanceAfter', width: 130, render: (a) => `¥${Number(a || 0).toFixed(2)}` },
    { title: '操作管理员', dataIndex: 'operatorName', key: 'operatorName', width: 160, render: (t) => t || '-' },
    { title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true, render: (t) => t || '-' }
  ];

  // Tab3 客服订单记录列。
  const orderColumns = [
    { title: '时间', dataIndex: 'createTime', key: 'createTime', width: 180, render: (t) => (t ? new Date(t).toLocaleString('zh-CN') : '-') },
    {
      title: '客服',
      key: 'agent',
      width: 220,
      render: (_, r) => <span>{r.agentName || `代理#${r.agentId}`}（ID {r.agentId}）</span>
    },
    { title: '玩家账号', dataIndex: 'accountName', key: 'accountName', width: 160, render: (t) => (t ? <Text copyable>{t}</Text> : '-') },
    { title: '账号ID', dataIndex: 'accountId', key: 'accountId', width: 120, render: (t) => t || '-' },
    { title: '金额', dataIndex: 'amount', key: 'amount', width: 110, render: (a) => <Text strong style={{ color: '#16a34a' }}>¥{Number(a || 0).toFixed(2)}</Text> },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, r) => {
        const meta = ADMIN_ORDER_STATUS[status] || { text: r.statusText || `其它(${status})`, color: 'default' };
        return <Tag color={meta.color}>{r.statusText || meta.text}</Tag>;
      }
    },
    { title: '通道', dataIndex: 'channelKey', key: 'channelKey', width: 140, render: (t) => t || '-' },
    { title: '订单号', dataIndex: 'orderNo', key: 'orderNo', width: 200, render: (t) => (t ? <Text code copyable>{t}</Text> : '-') },
    { title: '完成时间', dataIndex: 'finishedAt', key: 'finishedAt', width: 180, render: (t) => (t ? new Date(t).toLocaleString('zh-CN') : '-') },
    { title: '取消原因', dataIndex: 'cancelReason', key: 'cancelReason', width: 180, ellipsis: true, render: (t) => t || '-' }
  ];

  // Tab1 通道管理 = 现有全部内容(工具栏按钮 + 灰度说明 + 通道表), 行为样式零改动。
  const channelsTab = (
    <>
      <div className="page-header-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={loadChannels}>刷新</Button>
          <Button icon={<WalletOutlined />} onClick={() => { grantForm.resetFields(); setGrantVisible(true); }}>给客服发放平台币</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { createForm.resetFields(); setCreateVisible(true); }}>创建通道</Button>
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="灰度说明"
        description="通道要真正对玩家可见，需同时满足：环境变量 MANUAL_RECHARGE_ENABLED 主闸开启、通道 enabled=启用、且至少有一名成员开启接单。创建通道会自动把通道管理员标记为客服身份并赋默认权重 100。"
      />

      <Card className="table-card">
        <Table
          columns={columns}
          dataSource={channels}
          loading={loading}
          rowKey="id"
          pagination={false}
          size="middle"
          scroll={{ x: 900 }}
          locale={{ emptyText: '暂无通道，点击右上角“创建通道”。' }}
        />
      </Card>
    </>
  );

  // Tab2 发放记录。
  const grantsTab = (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            allowClear
            showSearch
            style={{ minWidth: 260 }}
            placeholder="按客服筛选（全部）"
            value={grantsFilterAgent}
            options={agentSelectOptions}
            optionFilterProp="label"
            filterOption={(input, option) => String(option?.label || '').toLowerCase().includes(input.toLowerCase())}
            onChange={(value) => setGrantsFilterAgent(value)}
          />
          <RangePicker
            value={grantsRange}
            onChange={(dates) => setGrantsRange(dates)}
            placeholder={['开始日期', '结束日期']}
          />
          <Button
            type="primary"
            onClick={() => loadGrants({ page: 1, pageSize: grantsPagination.pageSize, agentId: grantsFilterAgent, range: grantsRange })}
          >
            查询
          </Button>
          <Button
            onClick={() => {
              setGrantsFilterAgent(undefined);
              setGrantsRange(null);
              loadGrants({ page: 1, pageSize: grantsPagination.pageSize });
            }}
          >
            重置
          </Button>
        </Space>
      </Card>
      <Card className="table-card">
        <Table
          columns={grantColumns}
          dataSource={grants}
          loading={grantsLoading}
          rowKey="id"
          pagination={{
            current: grantsPagination.current,
            pageSize: grantsPagination.pageSize,
            total: grantsPagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `共 ${total} 条`
          }}
          onChange={handleGrantsTableChange}
          size="middle"
          scroll={{ x: 900 }}
          locale={{ emptyText: '暂无发放记录。' }}
        />
      </Card>
    </>
  );

  // Tab3 客服订单记录。
  const ordersTab = (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            allowClear
            showSearch
            style={{ minWidth: 260 }}
            placeholder="按客服筛选（全部）"
            value={ordersFilterAgent}
            options={agentSelectOptions}
            optionFilterProp="label"
            filterOption={(input, option) => String(option?.label || '').toLowerCase().includes(input.toLowerCase())}
            onChange={(value) => setOrdersFilterAgent(value)}
          />
          <Select
            allowClear
            style={{ minWidth: 140 }}
            placeholder="状态（全部）"
            value={ordersFilterStatus}
            onChange={(value) => setOrdersFilterStatus(value)}
            options={[
              { value: 0, label: '进行中' },
              { value: 1, label: '已完成' },
              { value: 2, label: '已取消' }
            ]}
          />
          <Input
            allowClear
            style={{ width: 200 }}
            placeholder="玩家账号搜索"
            value={ordersFilterAccount}
            onChange={(event) => setOrdersFilterAccount(event.target.value)}
            onPressEnter={() => loadOrders({ page: 1, pageSize: ordersPagination.pageSize, agentId: ordersFilterAgent, status: ordersFilterStatus, account: ordersFilterAccount, range: ordersRange })}
          />
          <RangePicker
            value={ordersRange}
            onChange={(dates) => setOrdersRange(dates)}
            placeholder={['开始日期', '结束日期']}
          />
          <Button
            type="primary"
            onClick={() => loadOrders({ page: 1, pageSize: ordersPagination.pageSize, agentId: ordersFilterAgent, status: ordersFilterStatus, account: ordersFilterAccount, range: ordersRange })}
          >
            查询
          </Button>
          <Button
            onClick={() => {
              setOrdersFilterAgent(undefined);
              setOrdersFilterStatus(undefined);
              setOrdersFilterAccount('');
              setOrdersRange(null);
              loadOrders({ page: 1, pageSize: ordersPagination.pageSize });
            }}
          >
            重置
          </Button>
        </Space>
      </Card>
      <Card className="table-card">
        <Table
          columns={orderColumns}
          dataSource={orders}
          loading={ordersLoading}
          rowKey="sessionNo"
          pagination={{
            current: ordersPagination.current,
            pageSize: ordersPagination.pageSize,
            total: ordersPagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `共 ${total} 条`
          }}
          onChange={handleOrdersTableChange}
          size="middle"
          scroll={{ x: 1500 }}
          locale={{ emptyText: '暂无客服订单记录。' }}
        />
      </Card>
    </>
  );

  // Tab4 客服余额: 所有人工客服(含通道管理员)+ 各自平台币钱包余额, 低余额高亮便于补充。
  const lowBalanceCount = useMemo(
    () => balances.filter((b) => Number(b.balance || 0) < LOW_BALANCE_THRESHOLD).length,
    [balances]
  );

  const balanceColumns = [
    {
      title: '客服',
      key: 'agent',
      render: (_, r) => (
        <Space wrap size={4}>
          <span>{r.nickname || r.agentName || `代理#${r.agentId}`}</span>
          <Text type="secondary">ID {r.agentId}</Text>
          {r.isChannelAdmin ? <Tag color="gold">通道管理员</Tag> : null}
          {r.isRechargeAgent ? <Tag color="green">客服</Tag> : <Tag>非客服</Tag>}
        </Space>
      )
    },
    { title: '账号', dataIndex: 'username', key: 'username', width: 180, render: (t) => (t ? <Text copyable>{t}</Text> : '-') },
    {
      title: '状态',
      key: 'state',
      width: 200,
      render: (_, r) => (
        <Space size={4}>
          {Number(r.status) === 1 ? <Tag color="blue">正常</Tag> : <Tag color="red">已禁用</Tag>}
          {Number(r.acceptingOrders) === 1 ? <Tag color="cyan">接单中</Tag> : <Tag>暂停接单</Tag>}
        </Space>
      )
    },
    {
      title: '平台币余额',
      dataIndex: 'balance',
      key: 'balance',
      width: 150,
      sorter: (a, b) => Number(a.balance || 0) - Number(b.balance || 0),
      render: (balance) => {
        const value = Number(balance || 0);
        const color = value <= 0 ? '#dc2626' : (value < LOW_BALANCE_THRESHOLD ? '#d97706' : '#16a34a');
        return <Text strong style={{ color }}>¥{value.toFixed(2)}</Text>;
      }
    }
  ];

  const balancesTab = (
    <>
      <div className="page-header-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={loadBalances}>刷新</Button>
          <Button icon={<WalletOutlined />} onClick={() => { grantForm.resetFields(); setGrantVisible(true); }}>给客服发放平台币</Button>
        </Space>
      </div>
      <Alert
        type={lowBalanceCount > 0 ? 'warning' : 'info'}
        showIcon
        style={{ marginBottom: 16 }}
        message="客服余额总览"
        description={`列出全部人工客服(含通道管理员)的平台币钱包余额（agent_coin_wallet，与发货/补单/发放同一账本）。红色=余额≤0，橙色=低于 ¥${LOW_BALANCE_THRESHOLD}（人工充值专接大额，单笔>500，建议及时补充）。当前共 ${balances.length} 名客服，其中 ${lowBalanceCount} 名余额偏低。`}
      />
      <Card className="table-card">
        <Table
          columns={balanceColumns}
          dataSource={balances}
          loading={balancesLoading}
          rowKey="agentId"
          pagination={false}
          size="middle"
          scroll={{ x: 800 }}
          locale={{ emptyText: '暂无人工客服。创建通道或配置成员权重后，客服将出现在此。' }}
        />
      </Card>
    </>
  );

  return (
    <div className="page-shell">
      <div className="page-header">
        <div className="page-header-main">
          <div className="page-title"><ApiOutlined style={{ marginRight: 8 }} />人工代充</div>
          <div className="page-subtitle">配置人工代充通道、成员加权与代理平台币余额。通道默认关闭，需手动启用。</div>
        </div>
      </div>

      <OrderLookupPanel onActiveChange={setUnifiedLookupActive} />
      {!unifiedLookupActive && <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        destroyInactiveTabPane={false}
        items={[
          { key: 'channels', label: '通道管理', children: channelsTab },
          { key: 'balances', label: '客服余额', children: balancesTab },
          { key: 'grants', label: '发放记录', children: grantsTab },
          { key: 'orders', label: '旧版客服会话记录', children: ordersTab }
        ]}
      />}

      {/* 创建通道 */}
      <Modal
        title={<span><PlusOutlined style={{ marginRight: 8 }} />创建人工充值通道</span>}
        open={createVisible}
        onCancel={() => setCreateVisible(false)}
        onOk={() => createForm.submit()}
        okText="创建"
        cancelText="取消"
        width={560}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="通道名称" rules={[{ required: true, message: '请输入通道名称' }]}>
            <Input placeholder="如：人工充值（客服A组）" maxLength={64} />
          </Form.Item>
          <Form.Item name="adminAgentId" label="通道管理员（代理）" rules={[{ required: true, message: '请选择通道管理员' }]} extra="该代理将成为本通道的客服身份与借扣起点，默认权重 100。">
            <Select
              showSearch
              placeholder="选择通道管理员代理"
              options={agentSelectOptions}
              optionFilterProp="label"
              filterOption={(input, option) => String(option?.label || '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          <Space size="large" style={{ display: 'flex' }}>
            <Form.Item name="displayNameEn" label="英文展示名" style={{ flex: 1 }}>
              <Input placeholder="Manual Recharge" maxLength={64} />
            </Form.Item>
            <Form.Item name="displayNameAr" label="阿拉伯语展示名" style={{ flex: 1 }}>
              <Input placeholder="اسم العرض" maxLength={64} />
            </Form.Item>
          </Space>
          <Space size="large" style={{ display: 'flex' }}>
            <Form.Item name="minAmount" label="最小金额(¥)" style={{ flex: 1 }}>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0 表示不限" />
            </Form.Item>
            <Form.Item name="maxAmount" label="最大金额(¥)" style={{ flex: 1 }}>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0 表示不限" />
            </Form.Item>
            <Form.Item name="sortOrder" label="排序" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      {/* 编辑通道 */}
      <Modal
        title={<span><EditOutlined style={{ marginRight: 8 }} />编辑通道</span>}
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        onOk={() => editForm.submit()}
        okText="保存"
        cancelText="取消"
        width={560}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item name="name" label="通道名称" rules={[{ required: true, message: '请输入通道名称' }]}>
            <Input maxLength={64} />
          </Form.Item>
          <Space size="large" style={{ display: 'flex' }}>
            <Form.Item name="displayNameEn" label="英文展示名" style={{ flex: 1 }}>
              <Input maxLength={64} />
            </Form.Item>
            <Form.Item name="displayNameAr" label="阿拉伯语展示名" style={{ flex: 1 }}>
              <Input maxLength={64} />
            </Form.Item>
          </Space>
          <Space size="large" style={{ display: 'flex' }}>
            <Form.Item name="minAmount" label="最小金额(¥)" style={{ flex: 1 }}>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="maxAmount" label="最大金额(¥)" style={{ flex: 1 }}>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="sortOrder" label="排序" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      {/* 成员权重 */}
      <Modal
        title={<span><SettingOutlined style={{ marginRight: 8 }} />成员权重配置{currentChannel ? ` · ${currentChannel.name}` : ''}</span>}
        open={weightVisible}
        onCancel={() => setWeightVisible(false)}
        onOk={handleSaveWeights}
        okText="保存权重"
        cancelText="取消"
        width={620}
        destroyOnClose
      >
        <Paragraph type="secondary">
          成员 = 通道管理员及其全部下级正式代理。权重为 0~100 的整数，所有成员权重之和必须等于 100。
          权重 &gt; 0 的成员保存后会自动标记为客服身份（is_recharge_agent=1）。
        </Paragraph>
        <Space style={{ marginBottom: 12 }}>
          <Button size="small" onClick={handleEqualize}>均分（余数归管理员）</Button>
          <Text type={weightSum === 100 ? 'success' : 'danger'}>当前合计：{weightSum} / 100</Text>
        </Space>
        <Table
          size="small"
          rowKey="agentId"
          pagination={false}
          dataSource={weightRows}
          locale={{ emptyText: '该通道暂无可配置成员' }}
          columns={[
            {
              title: '成员',
              dataIndex: 'nickname',
              key: 'nickname',
              render: (nickname, record) => (
                <Space>
                  <span>{nickname}</span>
                  {record.isAdmin ? <Tag color="gold">管理员</Tag> : null}
                  {record.isRechargeAgent ? <Tag color="green">客服</Tag> : <Tag>非客服</Tag>}
                  <Text type="secondary">ID {record.agentId}</Text>
                </Space>
              )
            },
            {
              // 需求1: 平台币钱包最新余额(发货/补单从此账本扣款)。0/负 标红提示需补充。
              title: '最新余额',
              dataIndex: 'balance',
              key: 'balance',
              width: 130,
              render: (balance) => {
                const value = Number(balance || 0);
                return <Text strong style={{ color: value <= 0 ? '#dc2626' : '#16a34a' }}>¥{value.toFixed(2)}</Text>;
              }
            },
            {
              title: '权重',
              dataIndex: 'weight',
              key: 'weight',
              width: 140,
              render: (weight, record) => (
                <InputNumber
                  min={0}
                  max={100}
                  precision={0}
                  value={weight}
                  onChange={(value) => handleWeightChange(record.agentId, value)}
                  style={{ width: '100%' }}
                />
              )
            }
          ]}
        />
      </Modal>

      {/* 给客服发放平台币 */}
      <Modal
        title={<span><WalletOutlined style={{ marginRight: 8 }} />给客服发放平台币</span>}
        open={grantVisible}
        onCancel={() => setGrantVisible(false)}
        onOk={() => grantForm.submit()}
        okText="确认加余额"
        cancelText="取消"
        width={520}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="该操作向代理独立卖方账本（agent_coin_wallet）贷记余额，与玩家平台币钱包彻底隔离。"
        />
        <Form form={grantForm} layout="vertical" onFinish={handleGrant}>
          <Form.Item name="agentId" label="目标客服" rules={[{ required: true, message: '请选择客服' }]}>
            <Select
              showSearch
              placeholder="选择要发放平台币的客服(仅未禁用客服)"
              options={grantAgentOptions}
              optionFilterProp="label"
              filterOption={(input, option) => String(option?.label || '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          <Form.Item name="amount" label="加余额金额(¥)" rules={[{ required: true, message: '请输入金额' }]}>
            <InputNumber min={0.01} precision={2} style={{ width: '100%' }} placeholder="正数" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={255} placeholder="可选，如充值渠道/凭证号" />
          </Form.Item>
        </Form>
        <Divider style={{ margin: '8px 0' }} />
        <Text type="secondary">同一操作可安全重试，requestId 幂等去重不会重复入账。</Text>
      </Modal>
    </div>
  );
}

export default ManualRechargeManagement;
